import { CURRENCIES, Money, RATE_BASES, type Currency, type ManualPriceInput, type RateBasis } from '@chukua/common';
import { Alert, Button, Form, Input, InputNumber, Modal, Select } from 'antd';
import { RATE_BASIS_LABELS } from '../../../../../shared/policies/rate-basis-labels';

const CURRENCY_OPTIONS = CURRENCIES.map((currency) => ({ value: currency, label: currency }));
const BASIS_OPTIONS = RATE_BASES.map((basis) => ({ value: basis, label: RATE_BASIS_LABELS[basis] }));

interface ManualPriceFormValues {
  amount: number;
  currency: Currency;
  basis: RateBasis;
  reason: string;
}

interface ManualPriceModalProps {
  open: boolean;
  jobNumber: string;
  error: string | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (input: ManualPriceInput) => Promise<boolean>;
}

/** The clerk's off-card price (gitaumoses4/chukua#35). The reason is what the approver reads, so it is required; the price is sent for approval and is not on the job until then. */
export function ManualPriceModal({ open, jobNumber, error, submitting, onCancel, onSubmit }: ManualPriceModalProps) {
  const [form] = Form.useForm<ManualPriceFormValues>();
  const currency = Form.useWatch('currency', form) ?? 'KES';

  async function submit(values: ManualPriceFormValues) {
    const isSent = await onSubmit({ amount: Money.parse(String(values.amount), values.currency).toJSON(), basis: values.basis, reason: values.reason });
    if (isSent) form.resetFields();
  }

  return (
    <Modal open={open} title={`Price ${jobNumber} off the card`} onCancel={onCancel} footer={null} destroyOnHidden>
      <Form<ManualPriceFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ currency: 'KES', basis: 'per_trip' }}
        onFinish={submit}
        disabled={submitting}
      >
        {error && <Alert className="mb-4" type="error" showIcon title={error} />}
        <div className="grid grid-cols-[1fr_auto] gap-3">
          <Form.Item name="amount" label="Price" rules={[{ required: true, message: 'Enter a price' }]}>
            <InputNumber className="w-full" min={0.01} precision={Money.decimalPlaces(currency)} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Select className="w-24" options={CURRENCY_OPTIONS} />
          </Form.Item>
        </div>
        <Form.Item name="basis" label="Per">
          <Select options={BASIS_OPTIONS} />
        </Form.Item>
        <Form.Item name="reason" label="Why is this off the card?" rules={[{ required: true, whitespace: true, message: 'Enter a reason' }]}>
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
        <Form.Item className="mb-0 flex justify-end">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Send for approval
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
